import clsx from 'clsx';
import { orderBy } from 'lodash';
import { arrayOf, bool, number, object, shape, string } from 'prop-types';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon, SanitizedHtml, Skeleton, Slide, Slider } from 'components';
import { isLink } from 'helpers';
import { useSafeState } from 'hooks';
import { appService } from 'services';

import 'assets/styles/components/_custom-slider.scss';

const CustomSlider = ({ slides, title = '', loading = false }) => {
  const { t } = useTranslation();
  const [activeSlideIndex, setActiveSlideIndex] = useSafeState(0);

  const orderedSlides = useMemo(() => orderBy(slides, 'order', ['asc']), [slides]);

  const handleControlClick = (clickOnNext) => {
    const nextSlideIndex = clickOnNext ? activeSlideIndex + 1 : activeSlideIndex - 1;

    if (nextSlideIndex >= slides.length) {
      setActiveSlideIndex(0);
    } else if (nextSlideIndex < 0) {
      setActiveSlideIndex(slides.length - 1);
    } else {
      setActiveSlideIndex(nextSlideIndex);
    }
  };

  const getSlideImage = (slideUrl) => {
    if (!slideUrl) return '';
    return isLink(slideUrl) ? slideUrl : `${appService.getStrapiBaseUrl()}${slideUrl}`;
  };

  return (
    <>
      {loading && (
        <div>
          <Skeleton type="text" size="initial" classNames="h-8 w-72 mb-4" />
          <div className="flex gap-2 items-center">
            <Skeleton type="text" size="initial" classNames="h-40 w-full lg:h-80 lg:w-[33rem] lg:grow" />
            <Skeleton type="text" size="initial" classNames="h-80 w-52 hidden lg:block" />
            <Skeleton type="text" size="initial" classNames="h-80 w-52 hidden lg:block" />
            <Skeleton type="text" size="initial" classNames="h-80 w-52 hidden lg:block" />
          </div>
        </div>
      )}

      {!loading && slides?.length > 0 && (
        <div>
          {/* MOBILE DESIGN */}
          {title && <h2 className="h2 underlined-yellow mb-4 lg:hidden">{title}</h2>}
          <Slider
            type="loop"
            options={{
              breakpoints: {
                640: {
                  padding: { left: '4rem', right: '4rem' }
                }
              },
              mediaQuery: 'min',
              perPage: 1,
              perMove: 1,
              gap: '1rem',
              padding: { left: '2rem', right: '2rem' },
              easing: 'cubic-bezier(0.05, 0.61, 0.41, 0.95)'
            }}
            sliderClass="custom-slider absolute-arrows top-right overflowing-slider pt-4 lg:hidden"
          >
            {slides.map((slide, i) => (
              <Slide slideClass="relative" key={`slider-${i}`}>
                <div
                  className="rounded-lg overflow-hidden w-full relative h-[200px] bg-cover"
                  style={{ backgroundImage: `url(${getSlideImage(slide.image?.url)})` }}
                >
                  <div className="absolute left-0 bottom-0 right-0 h-12 bg-black/25 backdrop-blur-[2px]" />
                  <div className="uppercase text-white truncate-line one-line absolute left-3 bottom-3 z-10">
                    {slide.title}
                  </div>
                </div>

                {!!slide?.description && (
                  <SanitizedHtml content={slide.description} className="slider-description p-4" />
                )}

                {!!slide?.cta && (
                  <a href={slide.cta.link} className="slider-btn btn text-sm w-fit mx-auto">
                    <span className="truncate-line two-lines sm:one-line">{slide.cta.buttonText}</span>
                  </a>
                )}
              </Slide>
            ))}
          </Slider>

          {/* DESKTOP DESIGN */}

          {/* CONTROL BUTTONS */}
          <div className="hidden lg:flex lg:flex-col">
            <div className={clsx('flex items-center gap-4 mb-8', title ? 'justify-between' : 'justify-end')}>
              {title && <h2 className="h2 underlined-yellow">{title}</h2>}

              <div className="flex items-center justify-end gap-4">
                <button type="button" className="btn control prev" onClick={() => handleControlClick(false)}>
                  <Icon name="angle-left" />
                  <span className="sr-only">{t('buttons.prev')}</span>
                </button>

                <button type="button" className="btn control next" onClick={() => handleControlClick(true)}>
                  <Icon name="angle-right" />
                  <span className="sr-only">{t('buttons.next')}</span>
                </button>
              </div>
            </div>

            <div className="custom-slider-container">
              {orderedSlides.map((slide, index) => {
                const isActive = index === activeSlideIndex;

                return (
                  <div
                    key={`custom-slide-${index}`}
                    className={clsx('slide', isActive && 'active')}
                    style={{ backgroundImage: `url(${getSlideImage(slide.image?.url)})` }}
                  >
                    <button type="button" className="sr-only-btn z-20" onClick={() => setActiveSlideIndex(index)}>
                      <span className="sr-only">{t('alts.slide', { index })}</span>
                    </button>
                    <div className={clsx('shadow', isActive && 'backdrop-blur-[2px]')}></div>
                    <div className="slide-content">
                      <div
                        className={clsx(
                          'uppercase text-2xl truncate-line',
                          isActive ? 'one-line' : 'max-w-[130px] xl:max-w-[190px] three-lines'
                        )}
                      >
                        {slide.title}
                      </div>
                      <div className="flex items-center justify-between gap-4">
                        {!!slide?.description && (
                          <SanitizedHtml
                            content={slide.description}
                            className="slide-description truncate-line two-lines"
                          />
                        )}

                        {!!slide?.cta && (
                          <a
                            href={slide.cta.link}
                            className={clsx('btn small text-sm', isActive ? 'visible' : 'invisible')}
                          >
                            <span className="truncate-line one-line">{slide.cta.buttonText}</span>
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

CustomSlider.propTypes = {
  slides: arrayOf(
    shape({
      order: number.isRequired,
      title: string.isRequired,
      description: string,
      image: object.isRequired,
      cta: shape({
        link: string,
        buttonText: string
      })
    })
  ).isRequired,
  title: string,
  loading: bool
};

export default CustomSlider;
